import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wrench, X } from 'lucide-react';

export const MAINTENANCE_TEXT = 'Some services are under maintenance right now. Please try again in a few minutes.';
const EVENT = 'altanon:service-trouble';

// Server failures and lost connections never reach the user as codes or technical text:
// the message becomes a plain maintenance note, and a small banner appears at the top of the page.
axios.interceptors.response.use(
  (response) => {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: false }));
    return response;
  },
  (error) => {
    const status = error.response?.status;
    if (!error.response || status >= 500) {
      if (error.response) {
        error.response.data = { ...(typeof error.response.data === 'object' ? error.response.data : {}), detail: MAINTENANCE_TEXT };
      } else {
        error.response = { status: 0, data: { detail: MAINTENANCE_TEXT } };
      }
      error.message = MAINTENANCE_TEXT;
      if (!axios.isCancel(error)) window.dispatchEvent(new CustomEvent(EVENT, { detail: true }));
    } else if (Array.isArray(error.response.data?.detail)) {
      // validation errors arrive as a list: show one readable sentence instead (a structured reply such as
      // {code: 'has_payments', ...} is meant for the page that asked, so it is left alone)
      error.response.data = { ...error.response.data, detail: 'Please check the details you entered and try again.' };
    }
    return Promise.reject(error);
  }
);

// A browser-level surprise (for example a failed background request) must not break the page.
window.addEventListener('unhandledrejection', (event) => {
  console.warn('Background task failed:', event.reason);
  event.preventDefault();
});

const ServiceNotice = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let timer;
    const onTrouble = (e) => {
      clearTimeout(timer);
      if (e.detail) setShow(true);
      else timer = setTimeout(() => setShow(false), 4000); // things work again: hide after a moment
    };
    window.addEventListener(EVENT, onTrouble);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(EVENT, onTrouble);
    };
  }, []);

  if (!show) return null;
  return (
    <div className="sticky top-0 z-[60] bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm font-figtree flex items-center justify-center gap-2" role="status" data-testid="service-notice">
      <Wrench className="w-4 h-4 shrink-0" />
      <span>{MAINTENANCE_TEXT}</span>
      <button className="ml-2 p-1 rounded hover:bg-amber-200" onClick={() => setShow(false)} aria-label="Hide message"><X className="w-4 h-4" /></button>
    </div>
  );
};

export default ServiceNotice;
